import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  Button,
  Box,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Feedback as FeedbackIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';

const CancelSubscriptionModal = ({ open, onClose, onConfirm, loading }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [step, setStep] = useState(1); // 1: Confirmation, 2: Feedback
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [improvements, setImprovements] = useState([]);

  const reasons = [
    { value: 'too_expensive', label: t('cancelModal.reasonTooExpensive') },
    { value: 'not_using', label: t('cancelModal.reasonNotUsing') },
    { value: 'missing_features', label: t('cancelModal.reasonMissingFeatures') },
    { value: 'technical_issues', label: t('cancelModal.reasonTechnicalIssues') },
    { value: 'found_alternative', label: t('cancelModal.reasonFoundAlternative') },
    { value: 'business_closed', label: t('cancelModal.reasonBusinessClosed') },
    { value: 'other', label: t('cancelModal.reasonOther') }
  ];

  const improvementOptions = [
    t('cancelModal.improvementMarketing'),
    t('cancelModal.improvementSupport'),
    t('cancelModal.improvementInterface'),
    t('cancelModal.improvementPrice'),
    t('cancelModal.improvementIntegrations'),
    t('cancelModal.improvementTraining')
  ];

  const handleImprovementChange = (improvement) => {
    setImprovements(prev => 
      prev.includes(improvement) 
        ? prev.filter(item => item !== improvement)
        : [...prev, improvement]
    );
  };

  const handleNext = () => {
    if (step === 1) {
      setStep(2);
    } else {
      // Submit feedback and cancel subscription
      const feedbackData = {
        reason,
        feedback,
        improvements
      };
      onConfirm(feedbackData);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
    } else {
      onClose();
    }
  };

  const resetModal = () => {
    setStep(1);
    setReason('');
    setFeedback('');
    setImprovements([]);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
          {step === 1 ? (
            <CancelIcon sx={{ fontSize: 48, color: 'warning.main' }} />
          ) : (
            <FeedbackIcon sx={{ fontSize: 48, color: 'primary.main' }} />
          )}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          {step === 1 ? t('cancelModal.title') : t('cancelModal.feedbackTitle')}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 3, sm: 4 }, py: 2 }}>
        {step === 1 ? (
          // Step 1: Confirmation
          <Box>
            <Typography variant="body1" sx={{ 
              textAlign: 'center', 
              mb: 3,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              lineHeight: 1.6,
              color: 'text.secondary'
            }}>
              {t('cancelModal.confirmMessage')}
            </Typography>

            <Box sx={{ 
              bgcolor: 'warning.light', 
              p: 3, 
              borderRadius: 2, 
              mb: 3,
              border: '1px solid',
              borderColor: 'warning.main'
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'warning.dark' }}>
                {t('cancelModal.importantTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'warning.dark', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                {t('cancelModal.importantInfo')}
              </Typography>
            </Box>
          </Box>
        ) : (
          // Step 2: Feedback
          <Box>
            <Typography variant="body1" sx={{ 
              textAlign: 'center', 
              mb: 3,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              color: 'text.secondary'
            }}>
              {t('cancelModal.feedbackMessage')}
            </Typography>

            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend" sx={{ fontWeight: 600, mb: 2 }}>
                {t('cancelModal.reasonQuestion')}
              </FormLabel>
              <RadioGroup
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                {reasons.map((reasonOption) => (
                  <FormControlLabel
                    key={reasonOption.value}
                    value={reasonOption.value}
                    control={<Radio />}
                    label={reasonOption.label}
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label={t('cancelModal.feedbackLabel')}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={t('cancelModal.feedbackPlaceholder')}
              sx={{ mb: 3 }}
            />

            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ fontWeight: 600, mb: 2 }}>
                {t('cancelModal.improvementsQuestion')}
              </FormLabel>
              <Box>
                {improvementOptions.map((improvement) => (
                  <FormControlLabel
                    key={improvement}
                    control={
                      <Checkbox
                        checked={improvements.includes(improvement)}
                        onChange={() => handleImprovementChange(improvement)}
                      />
                    }
                    label={improvement}
                    sx={{ display: 'block', mb: 0.5 }}
                  />
                ))}
              </Box>
            </FormControl>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Button
          onClick={handleBack}
          variant="outlined"
          size="large"
          startIcon={step === 2 ? <ArrowBackIcon /> : null}
          sx={{ 
            flex: { xs: 1, sm: 'none' },
            minWidth: { sm: 140 }
          }}
        >
          {step === 1 ? t('cancelModal.keepSubscription') : t('cancelModal.back')}
        </Button>
        
        <Button
          onClick={handleNext}
          color={step === 1 ? 'warning' : 'primary'}
          variant="contained"
          size="large"
          disabled={loading || (step === 2 && !reason)}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{ 
            flex: { xs: 1, sm: 'none' },
            minWidth: { sm: 180 }
          }}
        >
          {loading 
            ? t('cancelModal.canceling')
            : step === 1 
              ? t('cancelModal.continueCancel')
              : t('cancelModal.confirmCancel')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelSubscriptionModal;